import { useEffect, useMemo, useState, FormEvent } from 'react';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";
import CatalogModalFrame from './CatalogModalFrame';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';

interface StoreRecord {
    id: number;
    name: string;
    slug?: string | null;
    description?: string | null;
    address?: string | null;
    coordinates?: unknown;
    sort?: number | null;
    active?: boolean | number | null;
    show_home?: boolean | number | null;
    created_at?: string;
    updated_at?: string;
}

interface StoreFormState {
    name: string;
    slug: string;
    description: string;
    address: string;
    coordinates: string;
    sort: string;
    active: boolean;
    show_home: boolean;
}

interface ValidatedStoreForm {
    name: string;
    slug?: string;
    description?: string;
    address?: string;
    coordinates?: string;
    sort?: number;
    active: boolean;
    show_home: boolean;
}

const emptyForm = (): StoreFormState => ({
    name: '',
    slug: '',
    description: '',
    address: '',
    coordinates: '',
    sort: '0',
    active: true,
    show_home: false,
});

const toBoolean = (value: unknown): boolean => value === true || value === 1 || value === '1';

const sanitizeSlug = (value: string): string => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);

const formatCoordinates = (coordinates: unknown): string => {
    if (!coordinates) {
        return '';
    }

    if (typeof coordinates === 'string') {
        try {
            return JSON.stringify(JSON.parse(coordinates), null, 2);
        } catch {
            return coordinates;
        }
    }

    try {
        return JSON.stringify(coordinates, null, 2);
    } catch {
        return '';
    }
};

const extractStoreList = (response: any): Array<Pick<StoreRecord, 'id' | 'name'>> => {
    const payload = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

const extractStoreDetail = (response: any): StoreRecord | null => {
    const payload = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(payload)) {
        return payload[0] ?? null;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data[0] ?? null;
    }

    if (payload && typeof payload === 'object') {
        return payload;
    }

    return null;
};

const getApiErrorMessage = (err: any, fallback: string): string => {
    const message = err?.response?.data?.messages || err?.response?.data?.message || err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};

const validateStoreForm = (form: StoreFormState): { valid: true; data: ValidatedStoreForm; } | { valid: false; message: string; } => {
    const name = form.name.trim();
    const slug = sanitizeSlug(form.slug);
    const description = form.description.trim();
    const address = form.address.trim();
    const coordinates = form.coordinates.trim();
    const sortValue = form.sort.trim();

    if (!name) {
        return { valid: false, message: 'Store name is required.' };
    }

    if (form.slug.trim() && !slug) {
        return { valid: false, message: 'Slug can only contain letters, numbers, and hyphens.' };
    }

    if (slug.length > 100) {
        return { valid: false, message: 'Slug must be 100 characters or fewer.' };
    }

    let normalizedCoordinates: string | undefined;
    if (coordinates) {
        try {
            normalizedCoordinates = JSON.stringify(JSON.parse(coordinates));
        } catch {
            return { valid: false, message: 'Coordinates must be valid JSON.' };
        }
    }

    let normalizedSort: number | undefined;
    if (sortValue) {
        normalizedSort = Number(sortValue);
        if (!Number.isInteger(normalizedSort) || normalizedSort < 0) {
            return { valid: false, message: 'Sort order must be a whole number greater than or equal to 0.' };
        }
    }

    return {
        valid: true,
        data: {
            name,
            ...(slug ? { slug } : {}),
            ...(description ? { description } : {}),
            ...(address ? { address } : {}),
            ...(normalizedCoordinates ? { coordinates: normalizedCoordinates } : {}),
            ...(typeof normalizedSort === 'number' ? { sort: normalizedSort } : {}),
            active: form.active,
            show_home: form.show_home,
        }
    };
};

export default function CatalogStores() {
    const [allStores, setAllStores] = useState<StoreRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<StoreFormState>(emptyForm);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const totalItems = allStores.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const stores = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return allStores.slice(startIndex, startIndex + rowsPerPage);
    }, [allStores, currentPage, rowsPerPage]);

    useEffect(() => {
        refreshStores();
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const loadStores = async (): Promise<StoreRecord[]> => {
        const listResponse = await client.getApiCatalogStores({}, {
            params: { list: 1 }
        }) as any;

        const storeList = extractStoreList(listResponse);

        if (storeList.length === 0) {
            return [];
        }

        const detailResponses = await Promise.allSettled(
            storeList.map((store) => client.getApiCatalogStoreId({ id: String(store.id) }) as Promise<any>)
        );

        return detailResponses.map((result, index) => {
            const fallbackStore = storeList[index];

            if (result.status !== 'fulfilled') {
                return {
                    id: fallbackStore.id,
                    name: fallbackStore.name,
                };
            }

            const detailStore = extractStoreDetail(result.value);
            return {
                id: fallbackStore.id,
                name: fallbackStore.name,
                ...detailStore,
            };
        });
    };

    const refreshStores = async (options?: { focusStoreId?: number; targetPage?: number; }) => {
        setLoading(true);
        try {
            const nextStores = await loadStores();
            setAllStores(nextStores);

            if (options?.focusStoreId) {
                const focusedIndex = nextStores.findIndex((store) => store.id === options.focusStoreId);
                if (focusedIndex >= 0) {
                    setCurrentPage(Math.floor(focusedIndex / rowsPerPage) + 1);
                    return;
                }
            }

            if (options?.targetPage) {
                const nextTotalPages = Math.max(1, Math.ceil(nextStores.length / rowsPerPage));
                setCurrentPage(Math.min(options.targetPage, nextTotalPages));
            }
        } catch (err) {
            console.error('Failed to fetch stores:', err);
            setAllStores([]);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to load stores'));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (store: StoreRecord) => {
        setEditingId(store.id);
        setForm({
            name: store.name || '',
            slug: store.slug || '',
            description: store.description || '',
            address: store.address || '',
            coordinates: formatCoordinates(store.coordinates),
            sort: typeof store.sort === 'number' ? String(store.sort) : '0',
            active: toBoolean(store.active ?? true),
            show_home: toBoolean(store.show_home ?? false),
        });
        setIsModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm());
        setIsModalOpen(false);
    };

    const buildStorePayload = (data: ValidatedStoreForm, includeSort: boolean) => ({
        name: data.name,
        slug: data.slug,
        description: data.description,
        address: data.address,
        coordinates: data.coordinates,
        active: data.active,
        show_home: data.show_home,
        ...(includeSort && typeof data.sort === 'number' ? { sort: data.sort } : {}),
    });

    const createStore = async () => {
        const validated = validateStoreForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            const response = await client.postApiCatalogStoreCreate({
                postApiCatalogStoreCreateRequest: buildStorePayload(validated.data, false) as any
            }) as any;

            const createdStoreId = Number(response?.data?.data?.id ?? response?.data?.id);

            if (typeof validated.data.sort === 'number' && validated.data.sort !== 0 && Number.isFinite(createdStoreId)) {
                await client.postApiCatalogStoreUpdate({
                    postApiCatalogStoreUpdateRequest: {
                        id: createdStoreId,
                        sort: validated.data.sort,
                    } as any
                });
            }

            handleCancelEdit();
            await refreshStores(Number.isFinite(createdStoreId) ? { focusStoreId: createdStoreId } : undefined);
            showSuccessToast('Store created successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to create store'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateStore = async () => {
        const validated = validateStoreForm(form);
        if (!validated.valid || editingId === null) {
            await showValidationAlert(validated.valid ? 'Store ID is missing.' : validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogStoreUpdate({
                postApiCatalogStoreUpdateRequest: {
                    id: editingId,
                    ...buildStorePayload(validated.data, true),
                } as any
            });

            handleCancelEdit();
            await refreshStores({ focusStoreId: editingId });
            showSuccessToast('Store updated successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to update store'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (editingId !== null) {
            await updateStore();
            return;
        }

        await createStore();
    };

    const handleDelete = async (storeId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This store will be permanently deleted.',
            confirmButtonText: 'Delete Store'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogStoreDelete({
                postApiCatalogStoreDeleteRequest: { id: storeId }
            });

            if (editingId === storeId) {
                handleCancelEdit();
            }

            const nextPage = stores.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            await refreshStores({ targetPage: nextPage });
            showSuccessToast('Store deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to delete store'));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Stores</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Manage store details, storefront visibility, and ordering from the admin panel.
                    </p>
                </div>
                <button
                    onClick={() => {
                        handleCancelEdit();
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Store
                </button>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Store' : 'Create Store'}
                className="max-w-3xl"
                formId="catalog-store-form"
                submitLabel={submitting ? 'Processing...' : editingId !== null ? 'Update Store' : 'Create Store'}
                submitDisabled={submitting}
            >
                <form id="catalog-store-form" onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="e.g. Flagship Store"
                                required
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">
                                Slug
                            </label>
                            <input
                                type="text"
                                value={form.slug}
                                onChange={(e) => setForm({ ...form, slug: sanitizeSlug(e.target.value) })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="leave blank to auto-generate"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Letters, numbers, and hyphens only. The backend can generate this automatically on create.
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            placeholder="Short description for this store"
                            rows={3}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Address</label>
                            <textarea
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="Store location address"
                                rows={3}
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Coordinates JSON</label>
                            <textarea
                                value={form.coordinates}
                                onChange={(e) => setForm({ ...form, coordinates: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 font-mono text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder='{"lat": 25.2048, "lng": 55.2708}'
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Sort Order</label>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={form.sort}
                                onChange={(e) => setForm({ ...form, sort: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="0"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                value={form.active ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}
                                className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Homepage Visibility</label>
                            <select
                                value={form.show_home ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, show_home: e.target.value === 'true' })}
                                className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                                <option value="false">Hidden</option>
                                <option value="true">Shown on Home</option>
                            </select>
                        </div>
                    </div>
                </form>
            </CatalogModalFrame>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="border-b border-gray-200 bg-gray-50/50 p-5 dark:border-gray-800 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Existing Stores</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {loading ? 'Loading store records...' : `${totalItems} store${totalItems === 1 ? '' : 's'} loaded.`}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Store</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Address</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Sort</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Home</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Status</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-end text-xs font-bold uppercase tracking-wider text-gray-400">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        Loading stores...
                                    </TableCell>
                                </TableRow>
                            ) : stores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        No stores found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stores.map((store) => (
                                    <TableRow key={store.id} className={`group transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${editingId === store.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}`}>
                                        <TableCell className="px-6 py-4">
                                            <div className="space-y-1">
                                                <div className="font-bold text-gray-700 dark:text-gray-200">{store.name}</div>
                                                <div className="font-mono text-xs text-gray-500">{store.slug || 'No slug'}</div>
                                                {store.description ? (
                                                    <div className="line-clamp-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                                                        {store.description}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                            {store.address || 'No address'}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                                            {typeof store.sort === 'number' ? store.sort : 0}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <Badge color={toBoolean(store.show_home) ? 'info' : 'light'} size="sm">
                                                {toBoolean(store.show_home) ? 'Visible' : 'Hidden'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <Badge color={toBoolean(store.active ?? true) ? 'success' : 'error'} size="sm">
                                                {toBoolean(store.active ?? true) ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-end">
                                            <div className="flex justify-end gap-3 opacity-0 transition-opacity group-hover:opacity-100">
                                                <button
                                                    onClick={() => handleEdit(store)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-brand-200 hover:text-brand-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Edit Store"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(store.id)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-red-200 hover:text-red-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Delete Store"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {!loading && totalItems > 0 ? (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                ) : null}
            </div>
        </div>
    );
}
